import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { trackEvent } from '../../../../lib/analytics';

import {
    toggleModalDiagramRename,
    toggleModalDiagramClone,
    toggleModalDiagramDelete,
    toggleModalDiagramShare,
    toggleDiagramDetails,
    diagramPinToggle,
    diagramSoftDelete,
} from '../../../../redux/main/main.actions';
import { selectWorkspacePlan } from '../../../../redux/workspace/workspace.selectors';

function SidebarPinnedItem({ data: { id, title, access_role, is_pinned, pinned_at }, isCollapsed = false }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const btnRef = useRef(null);
    const menuRef = useRef(null);
    const [isOptionsHovered, setIsOptionsHovered] = useState(false);
    const [isOptionsClicked, setIsOptionsClicked] = useState(false);
    const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

    const plan = useSelector(selectWorkspacePlan);
    const isFreePlan = plan === 'free';
    const canEdit = ['edit', 'admin', 'owner'].includes(access_role) || isFreePlan;
    const canManage = ['admin', 'owner'].includes(access_role) || isFreePlan;
    const canDelete = ['admin', 'owner'].includes(access_role);
    const canArchive = ['admin', 'owner'].includes(access_role);

    const closeMenu = () => {
        setIsOptionsClicked(false);
        setIsOptionsHovered(false);
    };

    // Clamp the fixed-position menu to the viewport and close it when the
    // sidebar scrolls or the window resizes (a fixed menu would detach from
    // its anchor otherwise).
    useEffect(() => {
        if (!isOptionsClicked) return;

        if (menuRef.current) {
            const menuRect = menuRef.current.getBoundingClientRect();
            const maxTop = window.innerHeight - menuRect.height - 10;
            if (menuRect.top > maxTop) {
                setMenuPos((p) => ({ ...p, top: Math.max(10, maxTop) }));
            }
        }

        const handleAnchorMoved = () => closeMenu();
        window.addEventListener('resize', handleAnchorMoved);
        document.addEventListener('scroll', handleAnchorMoved, true);
        return () => {
            window.removeEventListener('resize', handleAnchorMoved);
            document.removeEventListener('scroll', handleAnchorMoved, true);
        };
    }, [isOptionsClicked]);

    const handleMenuItemClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        closeMenu();
    };

    const track = (action) => {
        trackEvent(`app_sidebar_pinned_${action}`, {
            page: 'app',
            type: 'sidebar',
            subtype: 'pinned_menu',
        });
    };

    const handleOptionsButtonClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        track('menu_click');
        if (!isOptionsClicked && btnRef.current) {
            const rect = btnRef.current.getBoundingClientRect();
            const topValue = rect.top + 34;
            const leftValue = rect.right - 133 + 107;
            setMenuPos({ top: topValue, left: leftValue });
        }
        setIsOptionsClicked((state) => !state);
    };

    const handleDiagramDetailsClick = (e) => {
        track('menu_details');
        dispatch(toggleDiagramDetails('main', id));
        handleMenuItemClick(e);
    };

    const handleOpenNewTabClick = (e) => {
        track('menu_new_tab');
        handleMenuItemClick(e);
        window.open(`/d/${id}`, '_blank');
    };

    const handlePinToggleClick = (e) => {
        const nextPinned = !is_pinned;
        track(nextPinned ? 'menu_pin' : 'menu_unpin');
        dispatch(diagramPinToggle({
            id,
            nextPinned,
            prevPinned: is_pinned,
            prevPinnedAt: pinned_at,
        }));
        handleMenuItemClick(e);
    };

    const handleRenameDiagramClick = (e) => {
        track('menu_rename');
        dispatch(toggleModalDiagramRename(id));
        handleMenuItemClick(e);
    };

    const handleCloneDiagramClick = (e) => {
        track('menu_duplicate');
        dispatch(toggleModalDiagramClone('main', id));
        handleMenuItemClick(e);
    };

    const handleShareDiagramClick = (e) => {
        track('menu_share');
        dispatch(toggleModalDiagramShare('main', id));
        handleMenuItemClick(e);
    };

    const handleSoftDeleteDiagramClick = (e) => {
        track('menu_soft_delete');
        dispatch(diagramSoftDelete(id));
        handleMenuItemClick(e);
    };

    const handleDeleteDiagramClick = (e) => {
        track('menu_delete');
        dispatch(toggleModalDiagramDelete(id));
        handleMenuItemClick(e);
    };

    return (
        <div
            className="menu-item-wrap menu-item-wrap-chats-item"
            onMouseEnter={() => setIsOptionsHovered(true)}
            onMouseLeave={() => {
                if (isOptionsClicked) return;
                setIsOptionsHovered(false);
            }}
        >
            <NavLink
                to={`/d/${id}`}
                className="menu-item-link"
                activeClassName="active"
                onClick={() => track('open')}
            >
                <div className={`menu-item menu-item-chat ${isOptionsHovered || isOptionsClicked ? 'hovered' : ''}`}>
                    <svg width="64px" height="64px" viewBox="0 1 24 24" fill="none" style={{ width: '16px', height: '17px', marginLeft: '-1px', marginRight: '-1px', marginTop: '-1px' }}><path d="M8.37658 15.6162L2.71973 21.273M11.6943 6.64169L10.1334 8.20258C10.0061 8.3299 9.9424 8.39357 9.86986 8.44415C9.80548 8.48905 9.73604 8.52622 9.66297 8.55488C9.58065 8.58717 9.49236 8.60482 9.3158 8.64014L5.65133 9.37303C4.69903 9.56349 4.22288 9.65872 4.00012 9.90977C3.80605 10.1285 3.71743 10.4212 3.75758 10.7108C3.80367 11.0433 4.14703 11.3866 4.83375 12.0733L11.9195 19.1591C12.6062 19.8458 12.9496 20.1891 13.282 20.2352C13.5716 20.2754 13.8643 20.1868 14.083 19.9927C14.3341 19.7699 14.4293 19.2938 14.6198 18.3415L15.3527 14.677C15.388 14.5005 15.4056 14.4122 15.4379 14.3298C15.4666 14.2568 15.5038 14.1873 15.5487 14.123C15.5992 14.0504 15.6629 13.9868 15.7902 13.8594L17.3511 12.2985C17.4325 12.2171 17.4732 12.1764 17.518 12.1409C17.5577 12.1093 17.5998 12.0808 17.6439 12.0557C17.6935 12.0273 17.7464 12.0046 17.8522 11.9593L20.3466 10.8903C21.0743 10.5784 21.4381 10.4225 21.6034 10.1705C21.7479 9.95013 21.7996 9.68163 21.7473 9.42335C21.6874 9.12801 21.4075 8.8481 20.8477 8.28827L15.7045 3.14514C15.1447 2.58531 14.8648 2.3054 14.5695 2.24552C14.3112 2.19317 14.0427 2.24488 13.8223 2.38941C13.5703 2.55469 13.4144 2.91854 13.1025 3.64624L12.0335 6.14059C11.9882 6.24641 11.9655 6.29932 11.9372 6.34893C11.912 6.393 11.8835 6.4351 11.8519 6.47484C11.8164 6.51958 11.7757 6.56029 11.6943 6.64169Z" strokeLinecap="round" strokeLinejoin="round" stroke="currentColor" strokeWidth="1.8px"></path></svg>
                    <span>{title}</span>
                </div>
            </NavLink>

            {!isCollapsed && (isOptionsHovered || isOptionsClicked) ?
                <button
                    ref={btnRef}
                    type="button"
                    className={`sidebar-pinned-options ${isOptionsClicked ? 'clicked' : ''}`}
                    aria-label={t('sidebar.diagram_options')}
                    onClick={(e) => { handleOptionsButtonClick(e); }}
                >
                    <svg height="28px" width="28px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 488 488" fill="#000000">
                        <g>
                            <circle cx="51.2" cy="244" r="50"></circle>
                            <circle cx="244" cy="244" r="50"></circle>
                            <circle cx="436.8" cy="244" r="50"></circle>
                        </g>
                    </svg>
                </button>
                : null
            }

            {isOptionsClicked && ReactDOM.createPortal(
                <React.Fragment>
                    <div
                        className="sidebar-pinned-menu-overlay"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            closeMenu();
                        }}
                    ></div>
                    <div
                        ref={menuRef}
                        className="sidebar-pinned-menu"
                        style={{ top: `${menuPos.top}px`, left: `${menuPos.left}px` }}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    >

                        <div className="control" style={{ paddingBottom: '2px' }}>
                            <button
                                className={`control-button t_btn_unpin`}
                                type="button"
                                aria-pressed={is_pinned ? 'true' : 'false'}
                                onClick={(e) => { handlePinToggleClick(e); }}
                            >
                                {is_pinned
                                    ? <svg width="64px" height="64px" viewBox="0 0 24 24" fill="none" style={{ width: '16px', marginRight: '-2px', height: '20px' }}>
                                        <path d="M17.1218 1.87023C15.7573 0.505682 13.4779 0.76575 12.4558 2.40261L9.75191 6.73289L11.1969 8.17793C11.2355 8.1273 11.2723 8.07415 11.3071 8.01845L14.1523 3.46191C14.493 2.91629 15.2528 2.8296 15.7076 3.28445L20.6359 8.21274C21.0907 8.66759 21.0041 9.42737 20.4584 9.76806L15.9019 12.6133C15.8462 12.6481 15.793 12.6848 15.7424 12.7234L17.1874 14.1684L21.5177 11.4645C23.1546 10.4424 23.4147 8.16307 22.0501 6.79852L17.1218 1.87023Z" fill="currentColor"></path>
                                        <path d="M3.56525 8.85242C3.6015 8.26612 3.84962 7.68582 4.32883 7.27422L5.77735 8.72274C5.75784 8.72967 5.73835 8.7368 5.71886 8.74414C5.64516 8.7719 5.61855 8.80285 5.60548 8.82181C5.58877 8.84604 5.56651 8.8937 5.56144 8.97583C5.55046 9.15333 5.62872 9.40686 5.82846 9.6066L14.3137 18.0919C14.5135 18.2916 14.767 18.3699 14.9445 18.3589C15.0266 18.3538 15.0743 18.3316 15.0985 18.3149C15.1175 18.3018 15.1484 18.2752 15.1762 18.2015C15.1835 18.182 15.1907 18.1625 15.1976 18.143L16.6461 19.5915C16.2345 20.0707 15.6542 20.3188 15.0679 20.3551C14.2853 20.4035 13.4808 20.0874 12.8995 19.5061L9.36397 15.9705L2.68394 22.6506C2.29342 23.0411 1.66025 23.0411 1.26973 22.6506C0.879206 22.26 0.879206 21.6269 1.26973 21.2363L7.94975 14.5563L4.41425 11.0208C3.83293 10.4395 3.51687 9.63502 3.56525 8.85242Z" fill="currentColor"></path>
                                        <path d="M2.00789 2.00786C1.61736 2.39838 1.61736 3.03155 2.00789 3.42207L20.5862 22.0004C20.9767 22.3909 21.6099 22.3909 22.0004 22.0004C22.391 21.6099 22.391 20.9767 22.0004 20.5862L3.4221 2.00786C3.03158 1.61733 2.39841 1.61733 2.00789 2.00786Z" fill="currentColor"></path>
                                    </svg>
                                    : <svg width="64px" height="64px" viewBox="0 0 24 24" fill="none" style={{ width: '17px', marginRight: '-2px', height: '21px' }}><path d="M8.37658 15.6162L2.71973 21.273M11.6943 6.64169L10.1334 8.20258C10.0061 8.3299 9.9424 8.39357 9.86986 8.44415C9.80548 8.48905 9.73604 8.52622 9.66297 8.55488C9.58065 8.58717 9.49236 8.60482 9.3158 8.64014L5.65133 9.37303C4.69903 9.56349 4.22288 9.65872 4.00012 9.90977C3.80605 10.1285 3.71743 10.4212 3.75758 10.7108C3.80367 11.0433 4.14703 11.3866 4.83375 12.0733L11.9195 19.1591C12.6062 19.8458 12.9496 20.1891 13.282 20.2352C13.5716 20.2754 13.8643 20.1868 14.083 19.9927C14.3341 19.7699 14.4293 19.2938 14.6198 18.3415L15.3527 14.677C15.388 14.5005 15.4056 14.4122 15.4379 14.3298C15.4666 14.2568 15.5038 14.1873 15.5487 14.123C15.5992 14.0504 15.6629 13.9868 15.7902 13.8594L17.3511 12.2985C17.4325 12.2171 17.4732 12.1764 17.518 12.1409C17.5577 12.1093 17.5998 12.0808 17.6439 12.0557C17.6935 12.0273 17.7464 12.0046 17.8522 11.9593L20.3466 10.8903C21.0743 10.5784 21.4381 10.4225 21.6034 10.1705C21.7479 9.95013 21.7996 9.68163 21.7473 9.42335C21.6874 9.12801 21.4075 8.8481 20.8477 8.28827L15.7045 3.14514C15.1447 2.58531 14.8648 2.3054 14.5695 2.24552C14.3112 2.19317 14.0427 2.24488 13.8223 2.38941C13.5703 2.55469 13.4144 2.91854 13.1025 3.64624L12.0335 6.14059C11.9882 6.24641 11.9655 6.29932 11.9372 6.34893C11.912 6.393 11.8835 6.4351 11.8519 6.47484C11.8164 6.51958 11.7757 6.56029 11.6943 6.64169Z" strokeLinecap="round" strokeLinejoin="round" stroke="currentColor" strokeWidth="1.8px"></path></svg>
                                }
                                {is_pinned ? t('sidebar.unpin') : t('sidebar.pin')}
                            </button>
                        </div>

                        <div className="horizontal-line"></div>

                        <div className="control" style={{ marginBottom: '-4px', paddingBottom: '1px' }}>
                            <button
                                className={`control-button t_btn_edit`}
                                type="button"
                                onClick={(e) => { handleDiagramDetailsClick(e); }}
                            >
                                {canEdit
                                    ?
                                    <>
                                        <svg style={{ marginRight: '-2px' }} width="20" height="20" viewBox="0.5 -0.5 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 21.4998L8.04927 19.3655C8.40421 19.229 8.58168 19.1607 8.74772 19.0716C8.8952 18.9924 9.0358 18.901 9.16804 18.7984C9.31692 18.6829 9.45137 18.5484 9.72028 18.2795L21 6.99982C22.1046 5.89525 22.1046 4.10438 21 2.99981C19.8955 1.89525 18.1046 1.89524 17 2.99981L5.72028 14.2795C5.45138 14.5484 5.31692 14.6829 5.20139 14.8318C5.09877 14.964 5.0074 15.1046 4.92823 15.2521C4.83911 15.4181 4.77085 15.5956 4.63433 15.9506L2.5 21.4998ZM2.5 21.4998L4.55812 16.1488C4.7054 15.7659 4.77903 15.5744 4.90534 15.4867C5.01572 15.4101 5.1523 15.3811 5.2843 15.4063C5.43533 15.4351 5.58038 15.5802 5.87048 15.8703L8.12957 18.1294C8.41967 18.4195 8.56472 18.5645 8.59356 18.7155C8.61877 18.8475 8.58979 18.9841 8.51314 19.0945C8.42545 19.2208 8.23399 19.2944 7.85107 19.4417L2.5 21.4998Z"></path></svg>
                                        {t('sidebar.edit_details')}
                                    </>
                                    :
                                    <>
                                        <svg style={{ marginRight: '-2px' }} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"></path></svg>
                                        {t('sidebar.see_details')}
                                    </>

                                }
                                {/* See details */}
                                {/* {canEdit ? 'Edit details' : 'See details'} */}
                            </button>
                        </div>

                        {canManage && <div className="control" style={{ paddingBottom: '1px' }}>
                            <button
                                className={`control-button t_btn_share`}
                                type="button"
                                onClick={(e) => { handleShareDiagramClick(e); }}
                            >
                                <svg width="22" height="22" viewBox="0 0 24 24" style={{ marginRight: '-1px' }} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M7 11C6.07003 11 5.60504 11 5.22354 11.1022C4.18827 11.3796 3.37962 12.1883 3.10222 13.2235C3 13.605 3 14.07 3 15V16.2C3 17.8802 3 18.7202 3.32698 19.362C3.6146 19.9265 4.07354 20.3854 4.63803 20.673C5.27976 21 6.11984 21 7.8 21H16.2C17.8802 21 18.7202 21 19.362 20.673C19.9265 20.3854 20.3854 19.9265 20.673 19.362C21 18.7202 21 17.8802 21 16.2V15C21 14.07 21 13.605 20.8978 13.2235C20.6204 12.1883 19.8117 11.3796 18.7765 11.1022C18.395 11 17.93 11 17 11M16 7L12 3M12 3L8 7M12 3V15"></path></svg>
                                {t('b.share')}
                            </button>
                        </div>}

                        {(canArchive || canDelete) && <div className="horizontal-line"></div>}

                        {canArchive && <div className="control" style={{ marginBottom: '-4px', paddingBottom: '1px' }}>
                            <button
                                className={`control-button`}
                                type="button"
                                onClick={(e) => { handleSoftDeleteDiagramClick(e); }}
                            >
                                <svg style={{ marginRight: '-1px' }} width="15px" height="23px" viewBox="0 0 24 24" fill="none"><g><path d="M3 12V15.8C3 16.9201 3 17.4802 3.21799 17.908C3.40973 18.2843 3.71569 18.5903 4.09202 18.782C4.51984 19 5.0799 19 6.2 19H17.8C18.9201 19 19.4802 19 19.908 18.782C20.2843 18.5903 20.5903 18.2843 20.782 17.908C21 17.4802 21 16.9201 21 15.8V12M3 12H6.67452C7.16369 12 7.40829 12 7.63846 12.0553C7.84254 12.1043 8.03763 12.1851 8.21657 12.2947C8.4184 12.4184 8.59136 12.5914 8.93726 12.9373L9.06274 13.0627C9.40865 13.4086 9.5816 13.5816 9.78343 13.7053C9.96237 13.8149 10.1575 13.8957 10.3615 13.9447C10.5917 14 10.8363 14 11.3255 14H12.6745C13.1637 14 13.4083 14 13.6385 13.9447C13.8425 13.8957 14.0376 13.8149 14.2166 13.7053C14.4184 13.5816 14.5914 13.4086 14.9373 13.0627L15.0627 12.9373C15.4086 12.5914 15.5816 12.4184 15.7834 12.2947C15.9624 12.1851 16.1575 12.1043 16.3615 12.0553C16.5917 12 16.8363 12 17.3255 12H21M3 12L5.32639 6.83025C5.78752 5.8055 6.0181 5.29312 6.38026 4.91755C6.70041 4.58556 7.09278 4.33186 7.52691 4.17615C8.01802 4 8.57988 4 9.70361 4H14.2964C15.4201 4 15.982 4 16.4731 4.17615C16.9072 4.33186 17.2996 4.58556 17.6197 4.91755C17.9819 5.29312 18.2125 5.8055 18.6736 6.83025L21 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path> </g></svg>
                                {t('b.archive')}
                            </button>
                        </div>}

                        {canDelete && <div className="control" style={{ paddingBottom: '1px' }}>
                            <button
                                className={`control-button control-button-danger`}
                                type="button"
                                onClick={(e) => { handleDeleteDiagramClick(e); }}
                            >
                                <svg style={{ marginLeft: '1px', marginRight: '-1px' }} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3H15M3 6H21M19 6L18.2987 16.5193C18.1935 18.0975 18.1409 18.8867 17.8 19.485C17.4999 20.0118 17.0472 20.4353 16.5017 20.6997C15.882 21 15.0911 21 13.5093 21H10.4907C8.90891 21 8.11803 21 7.49834 20.6997C6.95276 20.4353 6.50009 20.0118 6.19998 19.485C5.85911 18.8867 5.8065 18.0975 5.70129 16.5193L5 6M10 10.5V15.5M14 10.5V15.5"></path></svg>
                                {t('b.delete')}
                            </button>
                        </div>}
                    </div>
                </React.Fragment>,
                document.body
            )}
        </div>
    );
}

export default SidebarPinnedItem;
